import type { Role, User } from '@/api/types'

/**
 * Mirror of the backend's auth/permissions.py. The backend is the real
 * boundary; this only decides what to show. Superadmin passes everything.
 */
export const PERMISSIONS = {
  'cpo.admin': ['cpo'],
  'cpo.write': ['cpo', 'station_manager'],
  'station.write': ['cpo', 'station_manager'],
  'remote.control': ['cpo', 'station_manager', 'station_operator', 'viewer'],
  'analytics.read': ['cpo', 'station_manager'],
  'credits.manage': ['cpo'],
  'rfid.manage': ['cpo'],
  // Download and print a charger's QR sticker. Client-side only (the link is built from the charger id), so no backend counterpart.
  'charger.qr': ['cpo', 'station_manager', 'station_operator'],
  'pricing.manage': ['cpo'],
  // Station expenses: staff record their own station's, the CPO any.
  'expenses.write': ['cpo', 'station_manager', 'station_operator'],
  // Managers approve their station's (never their own), the CPO any.
  'expenses.approve': ['cpo', 'station_manager'],
  // NEA bills and electricity analysis: the owner, and managers for their
  // own station. Operators don't see electricity cost.
  'electricity.manage': ['cpo', 'station_manager'],
} as const satisfies Record<string, readonly Role[]>

export type Permission = keyof typeof PERMISSIONS

export const STATION_SCOPED: readonly Role[] = ['station_manager', 'station_operator', 'viewer']
/** Station roles that may only start charges with their own card. */
export const OWN_CARD_ONLY: readonly Role[] = ['station_operator', 'viewer']

export const isStationScoped = (u: Pick<User, 'role'> | null | undefined) => !!u && STATION_SCOPED.includes(u.role)
export const isSuperadmin = (u: Pick<User, 'role'> | null | undefined) => u?.role === 'superadmin'

/** Does the user hold the permission (and, for station roles, on this station)? */
export function can(user: Pick<User, 'role' | 'viewer_station_id'> | null | undefined, permission: Permission, stationId?: number | null) {
  if (!user) return false
  if (user.role === 'superadmin') return true
  if (!(PERMISSIONS[permission] as readonly Role[]).includes(user.role)) return false
  if (stationId != null && isStationScoped(user)) return user.viewer_station_id === stationId
  return true
}

/** Who may use the operator dashboard at all. Drivers use the mobile app. */
export const canUseDashboard = (u: Pick<User, 'role'> | null | undefined) => !!u && u.role !== 'user'

export const ROLE_LABEL: Record<Role, string> = {
  superadmin: 'Superadmin',
  cpo: 'Operator admin',
  station_manager: 'Station manager',
  station_operator: 'Station operator',
  viewer: 'Viewer',
  user: 'Driver',
}

export const ASSIGNABLE_STATION_ROLES = ['station_manager', 'station_operator', 'viewer'] as const
